import { useTranslation } from 'react-i18next';
import { useChat } from '@ai-sdk/react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { 
  X, 
  Send, 
  Sparkles, 
  Loader2, 
  Bot,
  User,
  MapPin,
  Calendar,
  Plane,
  Clock,
  Plus,
  PalmtreeIcon,
  Building2,
  Mountain,
  Heart
} from 'lucide-react';
import {
  Button,
  ScrollArea,
  cn,
  Textarea,
  useRepository,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  useLogin,
} from '@city-sage/aurum-avis-labs-components';
import { motion, AnimatePresence } from 'framer-motion';
import { QueryKeys } from '../../lib/QueryKeys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatHistoryModal } from './ChatHistoryModal';
import { ItineraryToolCard, DayToolCard, StopToolCard } from './ToolCallCards';
import { useAuth } from '@city-sage/aurum-avis-labs-components';
import { useIsAuthenticated } from '@azure/msal-react';
import { toast } from 'sonner';
  import { useToolCallHandling } from '../../lib/hooks/useToolCallHandling';
  import { useDebounce } from '../../lib/hooks/useDebounce';

interface ChatSheetProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
  initialInput?: string;
}

export function ChatSheet({ 
  isOpen, 
  onClose, 
  initialInput,
}: ChatSheetProps) {
  const { t } = useTranslation();
  const routerState = useRouterState();
  
  // Extract IDs from URL as single source of truth
  const threadId = routerState.location.search.chatThreadId;
  const initialPrompt = routerState.location.search['initial-prompt'];
  const itineraryId = useMemo(() => {
    const match = routerState.location.pathname.match(/^\/itineraries\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    return match ? match[1] : undefined;
  }, [routerState.location.pathname]);

  // Create a session key that only changes for true context switches, not internal threadId updates
  const [sessionId, setSessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  const [internalItineraryNavigation, setInternalItineraryNavigation] = useState(false);
  const [preservedItineraryId, setPreservedItineraryId] = useState<string | undefined>(itineraryId);
  
  const chatKey = useMemo(() => {
    // Only base the key on itinerary and session, NOT threadId
    // This prevents resets when threadId is updated internally by AI responses
    // Also prevent reset during internal itinerary navigation from tool calls
    if (internalItineraryNavigation) {
      return `preserving-${sessionId}`;
    }
    
    // Use preserved itinerary ID during session to maintain consistency
    const keyItineraryId = preservedItineraryId || itineraryId || 'no-itinerary';
    return `${keyItineraryId}_${sessionId}`;
  }, [itineraryId, sessionId, internalItineraryNavigation, preservedItineraryId]);

  // Function to force a new session (for New Chat button)
  const forceNewSession = useCallback(() => {
    setSessionId(Math.random().toString(36).substr(2, 9));
    setInternalItineraryNavigation(false);
    setPreservedItineraryId(itineraryId); // Update preserved ID to current
  }, [itineraryId]);

  // Update preserved itinerary ID when not in internal navigation
  useEffect(() => {
    if (!internalItineraryNavigation) {
      setPreservedItineraryId(itineraryId);
    }
  }, [itineraryId, internalItineraryNavigation, preservedItineraryId]);

  return (
    <ChatSheetContent
      key={chatKey} // Force reset only on itinerary changes or new sessions
      isOpen={isOpen}
      onClose={onClose}
      initialInput={initialInput || initialPrompt}
      threadId={threadId}
      itineraryId={itineraryId}
      onForceNewSession={forceNewSession}
      onInternalItineraryNavigation={setInternalItineraryNavigation}
    />
  );
}

interface ChatSheetContentProps {
  isOpen: boolean;
  onClose: () => void;
  initialInput?: string;
  threadId?: string;
  itineraryId?: string;
  onForceNewSession: () => void;
  onInternalItineraryNavigation: (value: boolean) => void;
}

function ChatSheetContent({ 
  isOpen, 
  onClose, 
  initialInput,
  threadId,
  itineraryId,
  onForceNewSession,
  onInternalItineraryNavigation,
}: ChatSheetContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showExamples, setShowExamples] = useState(true);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const routerState = useRouterState();
  
  // Login provider hook
  const { promptLoginModal } = useLogin();

  
  // Tool call handling
  const { 
    activeToolCalls, 
    currentToolCall, 
    handleToolCall, 
    clearToolCalls
  } = useToolCallHandling();

  // Note: Thread ID updates are handled by the key pattern for clean resets

  const examplePrompts = useMemo(() => {
    if (itineraryId) {
      return [
        {
          icon: PalmtreeIcon,
          text: 'Add a beach day in between my current itinerary',
          category: 'Itinerary Enhancement',
        },
        {
          icon: Calendar,
          text: 'Adjust my schedule to be more realistic with travel times',
          category: 'Time Management',
        },
        {
          icon: Building2,
          text: 'Add some local restaurants and food experiences',
          category: 'Dining & Culture',
        }
      ];
    }

    return [
      {
        icon: Building2,
        text: 'common.chat.examples.trip1',
        category: 'common.chat.categories.cityBreak',
      },
      {
        icon: Mountain,
        text: 'common.chat.examples.trip2',
        category: 'common.chat.categories.adventure',
      },
      {
        icon: Heart,
        text: 'common.chat.examples.trip3',
        category: 'common.chat.categories.romance',
      },
    ];
  }, [itineraryId]);

  // Handle thread ID updates from chat responses
  const handleThreadIdUpdate = useCallback((newThreadId: string | undefined) => {
    const currentSearch = routerState.location.search as Record<string, any>;
    const newSearch = { ...currentSearch };
    
    if (newThreadId) {
      newSearch.chatThreadId = newThreadId;
    } else {
      delete newSearch.chatThreadId;
    }
    
    navigate({
      to: window.location.pathname,
      search: newSearch,
      replace: true,
      resetScroll: false
    });
  }, [navigate, routerState.location.search]);

  // Auth hooks
  const { getToken } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { api } = useRepository();

  // Token state for auth
  const [token, setToken] = useState<string | null>(null);

  // Fetch account data for credits (only if authenticated)
  const { data: account } = useQuery({
    queryKey: QueryKeys.account.details(),
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated,
    staleTime: 1000 * 60, // 1 minute
  });

  const credits = account?.credits ?? null;

  const { data: initialMessages } = useQuery({
    queryKey: QueryKeys.chat.initialMessages(threadId, itineraryId),
    queryFn: async () => {
      // If threadId is 'new', start with empty messages
      if (threadId === 'new') {
        return [];
      }

      if (threadId) {
        return api.getChatHistory({ threadId });
      }

      if (itineraryId && account?.id) {
        const newestChat = await api.findAllAccountChat({ 
          accountId: account.id, 
          filter: JSON.stringify({ itineraryId }),
          sort:JSON.stringify(["createdAt", "DESC"]),
          limit: 1
        });
        if (newestChat[0]) {
          return api.getChatHistory({ threadId: newestChat[0].threadId });
        }
      }
      
      return [];
    },
    select: (data) => {
      // Aggregate consecutive assistant messages
      return data.reduce((acc: any[], message, index) => {
        if (index > 0 && 
            message.role === 'assistant' && 
            acc.length > 0 && 
            acc[acc.length - 1].role === 'assistant') {
          acc[acc.length - 1].content += '\n' + message.content;
        } else {
          acc.push({...message});
        }
        return acc;
      }, []);
    },
    enabled: (!!threadId || (!!itineraryId && !!account?.id)) && !!isAuthenticated,
  });

  // Fetch token when authentication changes
  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) {
        setToken(null);
        return;
      }
      try {
        const newToken = await getToken();
        setToken(newToken);
      } catch (error) {
        console.error('Failed to get token:', error);
        setToken(null);
      }
    };

    fetchToken();
  }, [isAuthenticated, getToken]);

  // Helper functions
  const parseApiError = (error: any) => {
    if (error?.message) return { message: error.message };
    if (typeof error === 'string') return { message: error };
    return { message: 'An unexpected error occurred' };
  };

  const isUnauthorizedError = (error: any) => {
    return error?.status === 401 || error?.message?.includes('401') || error?.message?.includes('Unauthorized');
  };

  const getPlaceholderText = () => {
    if (credits === 0 && isAuthenticated) return t('common.chat.placeholderCredits');
    return t('common.chat.placeholder');
  };

  const queryClient = useQueryClient();
  
  const scrollToBottom = () => {
    // messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize input from URL search params or initialInput
  const getInitialInputValue = () => {
    const urlInputParam = routerState.location.search['chat-input'];
    const urlInitialPrompt = routerState.location.search['initial-prompt'];
    
    // Priority: URL chat-input > initialInput prop > URL initial-prompt
    return urlInputParam || initialInput || urlInitialPrompt || '';
  };

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    status,
    error,
    setInput,
    stop,
  } = useChat({
    api: (() => {
      let endpoint = '/api/chat';
      // Don't include 'new' threadId in the URL path
      if (threadId && threadId !== 'new') {
        endpoint += `/${threadId}`;
      }
      if (itineraryId) {
        endpoint += `?itineraryId=${itineraryId}`;
      }
      return endpoint;
    })(),
    initialInput: getInitialInputValue(),
    initialMessages: initialMessages,
    experimental_prepareRequestBody: ({ messages: allMessages }: { messages: any[] }) => {
      const latestMessage = allMessages[allMessages.length - 1];
      return {
        messages: latestMessage ? [latestMessage] : [],
        threadId: threadId === 'new' ? undefined : threadId,
      };
    },
    headers: {
      ...(token && isAuthenticated ? { Authorization: `Bearer ${token}` } : {}),
    },
    onToolCall: handleToolCall,
    onResponse(response) {
      const responseThreadId = response.headers.get('x-thread-id');
      const currentSearch = routerState.location.search as Record<string, any>;
      const newSearch = { ...currentSearch };
      
      // Update thread ID if new one received
      if (responseThreadId && responseThreadId !== threadId) {
        newSearch.chatThreadId = responseThreadId;
      }
      
      // Clear URL input params after successful response
      if (newSearch['initial-prompt']) {
        delete newSearch['initial-prompt'];
      }
      if (newSearch['chat-input']) {
        delete newSearch['chat-input'];
      }
      
      // Only navigate if something actually changed
      const currentSearchStr = JSON.stringify(routerState.location.search);
      const newSearchStr = JSON.stringify(newSearch);
      if (currentSearchStr !== newSearchStr) {
        navigate({
          to: window.location.pathname,
          search: newSearch,
          replace: true,
          resetScroll: false
        });
      }
      
      scrollToBottom();
      setShowExamples(false);

      if (isAuthError) {
        setIsAuthError(false);
      }
    },
    onFinish: () => {
      scrollToBottom();
    
      // Use current itineraryId from URL for cache invalidation
      const currentItineraryMatch = window.location.pathname.match(/^\/itineraries\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      const currentItineraryId = currentItineraryMatch ? currentItineraryMatch[1] : undefined;
      
      if (currentItineraryId) {
        queryClient.invalidateQueries({
          predicate: (query) => JSON.stringify(query.queryKey).includes(currentItineraryId)
        });
      }

      // Execute pending navigation after stream completes
      const pendingNav = (window as any).pendingItineraryNavigation;
      if (pendingNav) {
        // Clear the pending navigation
        delete (window as any).pendingItineraryNavigation;
        
        // Navigate to the new itinerary, preserving search params
        navigate({
          to: pendingNav.path,
          search: pendingNav.search || {},
          resetScroll: false
        });
      }
    },
    onError(error) {
      const parsedError = parseApiError(error);
      queryClient.invalidateQueries({ queryKey: QueryKeys.account.details() });

      if (isUnauthorizedError(error)) {
        setIsAuthError(true);
        
        promptLoginModal({
          title: t('common.auth.title'),
          description: t('common.auth.description'),
        });
      } else {
        console.error('Chat error:', parsedError);
        toast.error(parsedError.message || t('common.chat.error'));
      }
    },
  });

  // Debounce input for URL syncing
  const debouncedInput = useDebounce(input, 500);

  // Sync debounced input to URL search params for input persistence
  useEffect(() => {
    // Only sync if chat is open and there's meaningful input
    if (!isOpen || !debouncedInput.trim()) {
      return;
    }

    // Don't sync during active chat streaming to avoid interference
    if (status === 'streaming' || status === 'submitted') {
      return;
    }

    // Don't sync if there are already messages (input is temporary)
    if (messages.length > 0) {
      return;
    }

    const currentSearch = routerState.location.search as Record<string, any>;
    const newSearch = { ...currentSearch };
    
    // Update or remove chat-input param
    if (debouncedInput.trim()) {
      newSearch['chat-input'] = debouncedInput.trim();
    } else if (newSearch['chat-input']) {
      delete newSearch['chat-input'];
    }

    // Only navigate if the param actually changed
    if (newSearch['chat-input'] !== currentSearch['chat-input']) {
      navigate({
        to: window.location.pathname,
        search: newSearch,
        replace: true,
        resetScroll: false
      });
    }
  }, [debouncedInput, isOpen, status, messages.length, navigate, routerState.location.search]);

  // Clear URL input param when chat is closed or reset
  useEffect(() => {
    if (!isOpen) {
      const currentSearch = routerState.location.search as Record<string, any>;
      if (currentSearch['chat-input']) {
        const newSearch = { ...currentSearch };
        delete newSearch['chat-input'];
        navigate({
          to: window.location.pathname,
          search: newSearch,
          replace: true,
          resetScroll: false
        });
      }
    }
  }, [isOpen, navigate, routerState.location.search]);

  // Note: Chat reset is now handled automatically by the key prop pattern in the parent component

  const handleExampleClick = (example: string) => {
    setInput(example);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleNewChat = () => {
    stop();
    
    // Clear active tool calls
    clearToolCalls();
    
    // Then force a new session which will reset all useChat state
    onForceNewSession();
    
    // Navigate with threadId set to 'new' for fresh chat
    const currentSearch = routerState.location.search as Record<string, any>;
    navigate({
      to: window.location.pathname,
      search: { 
        ...currentSearch,
        chatThreadId: 'new'
      },
      replace: true,
      resetScroll: false
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && status !== 'streaming') {
        handleSubmit(e as any);
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Tool call handling is now done via onToolCall callback in useChat

  return (
    <>
      <Drawer repositionInputs={false} open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="h-[90dvh]">
          {/* Fixed Header */}
          <DrawerHeader className="px-4 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <DrawerTitle className="text-left text-base font-medium leading-tight">
                    {t('common.chat.title')}
                  </DrawerTitle>
                  <p className="text-xs text-muted-foreground leading-tight">
                    {t('common.chat.subtitle')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  onClick={handleNewChat}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-label={t('common.chat.newChat')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  onClick={() => setIsHistoryOpen(true)}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full"
                  aria-label={t('common.chat.history.title')}
                >
                  <Clock className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DrawerHeader>

          {/* Scrollable Content Area */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
                {messages.length === 0 && showExamples && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-6"
                  >
                    <div className="text-center py-4">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center mx-auto mb-3 shadow-lg">
                        <PalmtreeIcon className="h-6 w-6 text-primary" />
                      </div>
                      <h4 className="font-medium text-base text-foreground mb-2">
                        {t('common.chat.welcomeTitle')}
                      </h4>
                      <p className="text-muted-foreground text-xs max-w-sm mx-auto">
                        {t('common.chat.welcomeDescription')}
                      </p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-xs font-medium text-foreground mb-2">
                        {t('common.chat.tryAskingAbout')}
                      </p>
                      {examplePrompts.map((example, index) => {
                        const Icon = example.icon;
                        return (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            onClick={() => handleExampleClick(t(example.text))}
                            className="w-full p-3 text-left rounded-xl border border-border bg-card hover:bg-accent hover:border-primary-200 transition-all group"
                          >
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                                <Icon className="h-4 w-4 text-primary" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-foreground text-xs sm:text-sm mb-0.5">{t(example.text)}</p>
                                <span className="text-xs text-primary font-medium">{t(example.category)}</span>
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                <AnimatePresence>
                  {messages.map((message, index) => {
                    const isUser = message.role === 'user';
                    const isLastMessage = index === messages.length - 1;
                    const shouldShowToolCall = !isUser && isLastMessage && currentToolCall;
                    
                    return (
                      <div key={index}>
                        {/* Show tool call above the agent message if it's the last message */}
                        {shouldShowToolCall && (
                          <AnimatePresence mode="wait">
                            <motion.div
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="mb-4"
                            >
                              {(() => {
                                switch (currentToolCall.type) {
                                  case 'itinerary':
                                    return (
                                      <ItineraryToolCard
                                        key={currentToolCall.id}
                                        status={currentToolCall.status}
                                        {...currentToolCall.data}
                                      />
                                    );
                                  case 'day':
                                    return (
                                      <DayToolCard
                                        key={currentToolCall.id}
                                        status={currentToolCall.status}
                                        {...currentToolCall.data}
                                      />
                                    );
                                  case 'stop':
                                    return (
                                      <StopToolCard
                                        key={currentToolCall.id}
                                        status={currentToolCall.status}
                                        {...currentToolCall.data}
                                      />
                                    );
                                  default:
                                    return null;
                                }
                              })()}
                            </motion.div>
                          </AnimatePresence>
                        )}

                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={cn(
                            'flex gap-3',
                            isUser ? 'justify-end' : 'justify-start'
                          )}
                        >
                          {!isUser && (
                            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
                              <Bot className="h-4 w-4" />
                            </div>
                          )}
                          <div
                            className={cn(
                              'max-w-[80%] rounded-2xl px-3 py-2 text-xs sm:text-sm sm:px-4 sm:py-3',
                              isUser
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-foreground border border-border'
                            )}
                          >
                            <div className="whitespace-pre-wrap break-words leading-relaxed">
                              {message.content}
                            </div>
                          </div>
                          {isUser && (
                            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                          )}
                        </motion.div>
                      </div>
                    );
                  })}
                </AnimatePresence>

                {/* Show tool call when there are no messages yet (initial state) */}
                {messages.length === 0 && currentToolCall && !showExamples && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      {(() => {
                        switch (currentToolCall.type) {
                          case 'itinerary':
                            return (
                              <ItineraryToolCard
                                key={currentToolCall.id}
                                status={currentToolCall.status}
                                {...currentToolCall.data}
                              />
                            );
                          case 'day':
                            return (
                              <DayToolCard
                                key={currentToolCall.id}
                                status={currentToolCall.status}
                                {...currentToolCall.data}
                              />
                            );
                          case 'stop':
                            return (
                              <StopToolCard
                                key={currentToolCall.id}
                                status={currentToolCall.status}
                                {...currentToolCall.data}
                              />
                            );
                          default:
                            return null;
                        }
                      })()}
                    </motion.div>
                  </AnimatePresence>
                )}


                {status === 'submitted' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-white" />
                    </div>
                    <div className="bg-muted border border-border rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-1">
                        <motion.div
                          className="w-2 h-2 bg-muted-foreground rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-muted-foreground rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-2 h-2 bg-muted-foreground rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 sm:p-4 rounded-2xl bg-destructive/10 border border-destructive/20"
                  >
                    <p className="text-xs sm:text-sm text-destructive">
                      {t('common.chat.error')}
                    </p>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Fixed Input Section */}
          <DrawerFooter className="p-3 sm:p-4">
            <form onSubmit={handleSubmit} className="relative">
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={getPlaceholderText()}
                  className="min-h-[44px] sm:min-h-[52px] max-h-[100px] sm:max-h-[120px] rounded-2xl resize-none pr-10 sm:pr-12 py-2 sm:py-3 text-base transition-all"
                  disabled={status === 'streaming' || (credits === 0 && isAuthenticated)}
                />
                
                <Button
                  type="submit"
                  disabled={!input.trim() || status === 'streaming' || status === 'submitted' || (credits === 0 && isAuthenticated)}
                  className="absolute right-1.5 sm:right-2 bottom-1.5 sm:bottom-2 h-7 w-7 sm:h-8 sm:w-8 rounded-full p-0 flex items-center justify-center shadow-sm z-10"
                  aria-label={t('common.chat.send')}
                >
                  {(status === 'streaming' || status === 'submitted') ? (
                    <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3 sm:h-4 sm:w-4" />
                  )}
                </Button>
              </div>
            </form>


            {/* Credits warning */}
            {credits === 0 && isAuthenticated && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-xl text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-red-800">
                    {t('common.chat.creditsRequired')}
                  </span>
                  <Link to="/profile">  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-red-600 hover:text-red-800 p-1"
                  >
                    {t('common.chat.getCredits')}
                  </Button>
                  </Link>
                </div>
              </div>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Chat History Modal */}
      <ChatHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onSelectThread={(selectedThreadId) => {
          handleThreadIdUpdate(selectedThreadId);
          setIsHistoryOpen(false);
        }}
      />

    </>
  );
} 